import React, { useEffect, useRef, useState } from 'react';
import { connectSocket, listenToNotification, disconnectSocket, emitGetNotification, emitGetNotificationCount, emitReadNotification } from '../../../service/socket.service';
import { alertIcon, noNotificationIcon, notificationIcon } from 'assets/utils/images';
import browserService from 'service/browser.service';
import { timeAgo } from 'utils/dateFormat';
import { useNavigate } from 'react-router-dom';

const Notification = () => {
    const [notification, setNotification] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef(null);
    const [hasMore, setHasMore] = useState(true);

    const navigate = useNavigate();

    const authDetails = JSON.parse(localStorage.getItem("authDetails") || "{}");
    const userId = authDetails._id;
    const userType = authDetails.userType;
    const authToken = browserService.getCookie('token');

    useEffect(() => {
        connectSocket(authToken, userId, userType);

        listenToNotification((response) => {
            const { success, data } = response;

            if (success && data.event === 'join') {
                fetchNotificationCount();
                fetchNotifications();
            }

            if (success && data.event === 'getNotification') {
                const { data: notificationList = [], totalData } = data?.data;
                if (notificationList.length) {
                    setNotification((prev) => {
                        const existingIds = new Set(prev.map((item) => item._id));
                        const newNotifications = notificationList.filter((item) => !existingIds.has(item._id));
                        return [...prev, ...newNotifications];
                    });
                    if (notificationList.length < 10) { setHasMore(false); }
                } else {
                    setHasMore(false);
                }
                setLoading(false);
            }

            if (success && data.event === 'readNotification') {
                const { type } = data?.data;
                if (type === 'all') {
                    setNotification((prev) =>
                        prev.map((item) => ({
                            ...item,
                            read: true,
                        }))
                    );
                }
            }

            if (success && data.event === 'getCounts') {
                const { notificationCount } = data?.data;
                if (notificationCount) {
                    setUnreadCount(notificationCount);
                } else {
                    setUnreadCount(0);
                }
                setLoading(false);
            }

            if (success && data.event === 'notification') {
                setNotification((pre) => [data.data, ...pre]);
            }
        });
    }, [authToken, userId]);

    const fetchNotifications = (lastId = undefined) => {
        if (loading) return;
        setLoading(true);

        emitGetNotification(userId, lastId);
        setLoading(false);
    };

    const fetchNotificationCount = () => {
        emitGetNotificationCount(userId, userType);
    };

    const handleScroll = () => {
        const container = containerRef.current;
        if (container) {
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - 10) {
                if (hasMore && !loading) {
                    const lastId = notification.length > 0 ? notification[notification.length - 1]._id : null;
                    fetchNotifications(lastId);
                }
            }
        }
    };

    const handleClick = (_id, reference) => {
        emitReadNotification('', _id);
        navigate(`/sell-invoice/preview/${reference}`);
    };

    const handleMarkAllRead = () => {
        emitReadNotification('all');
    };

    const Component = ({ headerTitle, data }) => {
        return (
            <div className="mb-5">
                <div className="h-[68px] flex items-center border-b border-[rgba(0,0,0,0.1)] px-[20px] justify-between">
                    <div className="flex flex-row items-center gap-2 relative">
                        <div className="p-2 border rounded-full relative border-blue-500">
                            <img className="h-6 w-6" src={notificationIcon} alt="Notification icon" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-medium rounded-full h-6 w-6 flex items-center justify-center">
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                        </div>
                        <h3 className="text-lg font-medium">{headerTitle}</h3>
                    </div>

                    {/* Mark All Read Button */}
                    <button className="text-blue-500 text-[14px] font-medium hover:underline" onClick={handleMarkAllRead}>
                        Mark all read
                    </button>
                </div>

                {data.length > 0 ? (
                    <ul
                        ref={containerRef}
                        onScroll={handleScroll}
                        className="list-none p-0 m-0 flex-1 h-[calc(100vh-68px)] overflow-y-auto"
                    >
                        {data.map(({ _id, reference, content, createdAt, read }, index) => (
                            <li
                                key={index}
                                className={`cursor-pointer flex flex-row items-center gap-2 px-4 py-2 shadow-sm transition-all overflow-hidden ${!read ? 'bg-[#F0F8FF]' : 'bg-white'} border-b`}
                                onClick={() => handleClick(_id, reference)}
                            >
                                <div className={`w-[6rem] h-[3rem] rounded-[12px] ${index % 2 === 0 ? 'bg-[#EDEEFC]' : 'bg-[#E6F1FD]'} flex items-center justify-center`}>
                                    <img className="w-6 h-6 object-contain" src={alertIcon} alt='Alert' />
                                </div>
                                <div className="flex flex-col overflow-hidden">
                                    <span className="text-sm font-medium text-gray-800">{content}</span>
                                    <span className="text-xs text-gray-500 mt-1">{timeAgo(createdAt)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center mt-[70%]">
                        <img className="w-[70%]" src={noNotificationIcon} alt="no-notification" />
                        <h2 className="text-[20px] font-semibold text-center">No Notification</h2>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="max-w-[250px] w-full lg:max-w-[400px] h-[100vh] bg-[#fff] box-border transition-all duration-300 ease-in-out border-l border-[rgba(0,0,0,0.1)]">
            <Component
                headerTitle={"Notifications"}
                data={notification}
            />
        </div>
    );
};

export default Notification;